// ══════════════════════════════════════════════════════════════════════════════
//  Mapa unico de estatus (STATUS_WEB, espejo de STATUS_PDF), Badge y los mapeos legados estatusBadge / cobro_badge.
// ══════════════════════════════════════════════════════════════════════════════
import { C, R, F } from '../../design.js';
import { Icon } from './Icon.js';
import { etiquetaCobro, colorCobro } from '../../modules/estimaciones/estatus.cobro.js';

// ── Mapa de estatus ÚNICO (web) — espejo de STATUS_PDF en design-pdf.js ──
//  Mismas keys y semántica; aquí en tokens `C`, allá en triples RGB para jsPDF.
//  Lo consumen Badge (status=), celdas de tabla, charts y —vía su espejo— el PDF.
//  Fase 3 migra los call sites de estatusBadge/cobro_badge a este mapa.
const STATUS_WEB = {
  // Cobranza / estimaciones
  'status-paid':      { bg:C.successLight,  fg:C.success,  icon:'check-circle',   label:'Pagada' },
  'status-sent':      { bg:C.primaryLight,  fg:C.primary,  icon:'send',           label:'Enviada' },
  'status-pending':   { bg:C.warningLight,  fg:C.warning,  icon:'clock',          label:'En espera' },
  'status-disputed':  { bg:C.dangerLight,   fg:C.danger,   icon:'alert-triangle', label:'En disputa' },
  'status-cancelled': { bg:C.surfaceSunken, fg:C.textSub,  icon:'x',              label:'Cancelada' },
  // QC (concreto / compactación)
  'cumple':           { bg:C.successLight,  fg:C.success,  icon:'check-circle',   label:'Cumple' },
  'no-cumple':        { bg:C.criticalLight, fg:C.critical, icon:'alert-triangle', label:'No cumple' },
  'pendiente':        { bg:C.warningLight,  fg:C.warning,  icon:'clock',          label:'Pendiente' },
  'hoy':              { bg:C.accentLight,   fg:C.accent,   icon:'registro',       label:'Hoy' },
  'capturado':        { bg:C.successLight,  fg:C.success,  icon:'check-circle',   label:'Capturado' },
  // Despacho QC: una muestra lista viaja en la entrega; una bloqueada es
  // TRABAJO PENDIENTE (ambar), no una inconsistencia — el rojo se guarda para
  // la entrega sin confirmar, que si es un dato que no cuadra.
  'qc-lista':         { bg:C.successLight,  fg:C.success,  icon:'check-circle',   label:'Lista' },
  'qc-bloqueada':     { bg:C.warningLight,  fg:C.warning,  icon:'lock',           label:'Bloqueada' },
  // Ciclo de vida de comp_reportes (solo web; sin espejo en STATUS_PDF a
  // propósito — el PDF de compactación no imprime el estado del reporte).
  'comp-borrador':    { bg:C.surfaceSunken, fg:C.textSub,  icon:'pencil',         label:'Borrador' },
  'comp-preliminar':  { bg:C.warningLight,  fg:C.warning,  icon:'clock',          label:'Preliminar' },
  'comp-definitivo':  { bg:C.successLight,  fg:C.success,  icon:'check-circle',   label:'Definitivo' },
  'comp-entregado':   { bg:C.purpleLight,   fg:C.purple,   icon:'send',           label:'Entregado' },
};

// Badge — dos APIs:
//  • semántica v4:  <Badge status="status-paid" icon count={3} />  (mapa STATUS_WEB)
//  • legacy color:  <Badge label="…" color="green" />             (retrocompat)
// Par [relleno claro, color saturado] de cada estado. Un solo dueño: lo consume
// el Badge y cualquier superficie que dibuje ese mismo estado (las barras del
// calendario de estimaciones), para que no existan dos verdades por estado.
const BADGE_COLORS = {
  green:  [C.successLight, C.success],  blue:   [C.primaryLight, C.primary],
  yellow: [C.warningLight, C.warning],  red:    [C.dangerLight,  C.danger],
  teal:   [C.accentLight,  C.accent],   purple: [C.purpleLight,  C.purple],
  gray:   [C.surfaceSunken,C.textSub],  orange: [C.markLight,    C.markDeep],
  critical:[C.criticalLight,C.critical],
  // rampa1 / rampa2: las etapas que fluyen en la cartera (Por enviar, Por cobrar),
  // los mismos pasos de la rampa azul del Inicio (etapa.cobro.js).
  rampa1: [C.rampa1Light, C.rampa1Deep],  rampa2: [C.rampa2Light, C.rampa2Deep],
  // navy: el azul más profundo de la marca. Nació para el período EN CURSO del
  // calendario, que tenía que distinguirse del azul de "facturada" estando los
  // dos en el mismo carril.
  navy:   [C.primaryLight, C.primaryDeep],
  // steel: azul acero MEDIO. Unico par cuyo relleno no es un pastel casi
  // blanco, para estados que deben separarse por densidad y no por tono.
  steel:  [C.primaryMid,   C.primaryDeep],
};

const Badge = ({ label, color="gray", size="sm", status, icon, count }) => {
  const fs = size==="xs"?10:11;
  const padY = size==="xs"?"1px 6px":"2px 8px";
  if (status && STATUS_WEB[status]) {
    const s = STATUS_WEB[status];
    const ic = icon===false ? null : (typeof icon==="string" ? icon : s.icon);
    return (
      <span className="gb-estado" style={{display:"inline-flex",alignItems:"center",gap:4,background:s.bg,color:s.fg,borderRadius:R.xs,padding:padY,fontSize:fs,fontWeight:600,letterSpacing:0.1,whiteSpace:"nowrap"}}>
        {ic && <Icon name={ic} size={12} color="currentColor"/>}
        {label ?? s.label}
        {count!=null && count!=="" && <span style={{...F.num,fontWeight:700,opacity:0.75}}>{count}</span>}
      </span>
    );
  }
  const [bg,text] = BADGE_COLORS[color]||BADGE_COLORS.gray;
  return <span className="gb-estado" style={{background:bg,color:text,borderRadius:R.xs,padding:padY,fontSize:fs,fontWeight:600,letterSpacing:0.1,whiteSpace:"nowrap"}}>{label}</span>;
};

const estatusBadge = e => {
  const map = {"Activa":["Activa","green"],"Cerrada":["Cerrada","gray"],"Pausada":["Pausada","yellow"],"En Cobranza":["Cobranza","blue"]};
  const [l,c] = map[e]||[e,"gray"];
  return <Badge label={l} color={c}/>;
};

// El texto y el color salen del diccionario único del dinero
// (estimaciones/facturacion.logic.js): este mapa decía "Pendiente Factura"
// donde la cartera decía "En espera de factura" y el buscador "Por facturar",
// pintaba EN DISPUTA en teal mientras el resto lo pintaba en rojo, y no conocía
// los dos estatus parciales (caían en gris con el texto crudo de la base).
const cobro_badge = e => <Badge label={etiquetaCobro(e)} color={colorCobro(e)}/>;


export { BADGE_COLORS, Badge, STATUS_WEB, cobro_badge, estatusBadge };
